import { useStyles } from "./Paywall.style";
import {
  FC,
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { Typography } from "../../components/core/Typography/Typography";
import MobileImage from "../../assets/subscription/console_image_mb_1x.webp";
import MobileImage2x from "../../assets/subscription/console_image_mb_2x.webp";
import MobileImage3x from "../../assets/subscription/console_image_mb_3x.webp";
import MainBG1x from "../../assets/subscription/main_background_1x.webp";
import MainBG2x from "../../assets/subscription/main_background_2x.webp";
import MainBG3x from "../../assets/subscription/main_background_3x.webp";
import Controller1x from "../../assets/subscription/controller_1x.webp";
import Controller2x from "../../assets/subscription/controller_2x.webp";
import Controller3x from "../../assets/subscription/controller_3x.webp";
import FlipkartLogo1x from "../../assets/subscription/flipkart-logo_1x.webp";
import FlipkartLogo2x from "../../assets/subscription/flipkart-logo_2x.webp";
import FlipkartLogo3x from "../../assets/subscription/flipkart-logo_3x.webp";
import { StaticImage } from "../../components/core/StaticImage/StaticImage";
import { TypographyColor, TypographyVariant } from "../../constants/typography";
import Button from "../../components/core/Button/Button";
import clsx from "classnames";
import { useQuery } from "@apollo/client";
import {
  getSubscriptionPlansGQL,
  meAAAGQL,
  Query,
  SubscriptionPlan,
  User,
} from "@onmo/graphql/graphql";
import { setSkippedActivationCode } from "../../models/storage";
import { AnalyticsGA4 } from "../../models/analyticsGA4";
import ActivateQrPopup from "../../components/common/ActivateQrPopup/ActivateQrPopup";
import ConfirmSubscriptionPopup from "../../components/common/ConfirmSubscriptionPopup/ConfirmSubscriptionPopup";
import BillingInfoPopup from "../../components/common/BillingInfoPopup/BillingInfoPopup";
import { isTV } from "../../utils/platform";
import { PageRoutes } from "../../constants/routes";
import { SubscribePlanContent } from "../../constants/payment";
import { Icon } from "../../components/core/Icon/Icon";
import {
  useActiveElement,
  useGamepadContext,
} from "../../contexts/GamepadProvider";
import { SliderContainer } from "../../components/common/SliderContainer";
import { SLIDER_SETTINGS } from "../../constants/console";
import { useAppSelector } from "../../components/hooks";
import {
  selectIsDesktopView,
  selectIsMobile,
} from "../../store/slices/appState";
import SubscriptionTv from "../../assets/subscription/paywall_bg.webp";
import { useSubscriptionFlow } from "../../hooks/useSubscriptionFlow";
import { LoadingScreen } from "../../components/common/LoadingScreen/LoadingScreen";
import { useBackNavigation } from "../../hooks/useBackNavigation";
import AccountMismatchPopup from "../../components/common/AccountMismatchPopup/AccountMismatchPopup";

const Paywall: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const classes = useStyles();
  const location = useLocation();
  const goBack = useBackNavigation();
  const [activateQRLink, setActivateQRLink] = useState("");
  const [isMismatchPopupOpen, setIsMismatchPopupOpen] = useState(false);
  const [showingPlan, setShowingPlan] = useState<
    "essential_pass_bundle" | "console_only"
  >("essential_pass_bundle");
  const { fromTo } = (location.state ?? {}) as {
    fromTo: "onboarding" | "console" | "game";
  };
  const { data: dataSubscriptionPlans, loading: loadingPlans } =
    useQuery<Query>(getSubscriptionPlansGQL, {
      variables: { input: { productId: "aaa" } },
    });

  const { data: meData, loading: loadingMe } = useQuery<{ me: User }>(meAAAGQL);

  const isLoading = loadingPlans || loadingMe;

  const isFounder = meData?.me?.metadata?.tags?.includes("op_founders");

  const tvUserId = useMemo(
    () => new URLSearchParams(location.search).get("tvUserId"),
    [location.search],
  );

  const isWithController =
    showingPlan === "essential_pass_bundle" && !isFounder;
  const activeElement = useActiveElement();
  const isDesktop = useAppSelector(selectIsDesktopView);
  const isMobile = useAppSelector(selectIsMobile);
  const { setCallback, cleanup, setActiveElement } = useGamepadContext();

  const { withoutController, firstPlan } = useMemo(() => {
    const plans = dataSubscriptionPlans?.subscriptionPlans;
    const withoutController = plans?.items.find(
      (plan: SubscriptionPlan) => plan?.metadata?.type === "without_controller",
    );
    const firstPlan = plans?.items.find(
      (plan: SubscriptionPlan) =>
        plan?.metadata?.type === "with_controller" &&
        plan?.metadata?.shouldDisplay,
    );
    return {
      withoutController,
      firstPlan,
    };
  }, [dataSubscriptionPlans]);

  useEffect(() => {
    if (!tvUserId || isTV || !meData?.me?.id) return;
    if (tvUserId !== meData.me.id) {
      setIsMismatchPopupOpen(true);
    }
  }, [tvUserId, meData?.me?.id]);

  const {
    start,
    close: closeSubscriptionFlow,
    confirm: confirmSubscription,
    submitBilling,
    plan: selectedPlan,
    isConfirmOpen,
    isBillingOpen,
  } = useSubscriptionFlow();

  const handleClose = useCallback(() => {
    AnalyticsGA4.paywallSelect("skip");
    setSkippedActivationCode();
    goBack();
  }, [goBack]);

  const subscribeProController = () => {
    AnalyticsGA4.paywallSelect("bundle");
    if (isTV) {
      setActivateQRLink(firstPlan?.metadata?.purchaseUrl);
    } else {
      window.open(firstPlan?.metadata.purchaseUrl, "_blank");
    }
  };

  const subscribeWithoutController = () => {
    if (!withoutController) return;
    AnalyticsGA4.paywallSelect("subscribe");
    if (isTV) {
      const urlPaywall = new URL(
        `${import.meta.env.VITE_APP_URL}${PageRoutes.Paywall}`,
      );
      if (meData?.me?.id) urlPaywall.searchParams.set("tvUserId", meData.me.id);

      setActivateQRLink(urlPaywall.toString());
      return;
    }
    start(withoutController);
  };

  useEffect(() => {
    setCallback({ onBackAction: handleClose });
    return () => {
      cleanup();
    };
  }, [setCallback, cleanup, setActiveElement, handleClose]);

  const renderPlanFeatures = () => {
    return (
      <div className={classes.planFeatures}>
        <Typography
          variant={
            isMobile
              ? TypographyVariant.BodyB6Bold
              : TypographyVariant.BodyB4Bold
          }
          className={clsx(classes.planItem, {
            active: showingPlan === "essential_pass_bundle",
            [classes.selected]:
              activeElement?.getAttribute("data-gamepad") === "paywall-bundle",
          })}
          onClick={() => setShowingPlan("essential_pass_bundle")}
          data-gamepad="paywall-bundle"
          data-selected-class={classes.selected}
        >
          Pro Controller Bundle
        </Typography>
        <Typography
          variant={
            isMobile
              ? TypographyVariant.BodyB6Bold
              : TypographyVariant.BodyB4Bold
          }
          className={clsx(classes.planItem, {
            active: showingPlan === "console_only",
            [classes.selected]:
              activeElement?.getAttribute("data-gamepad") === "paywall-console",
          })}
          onClick={() => setShowingPlan("console_only")}
          data-gamepad="paywall-console"
          data-selected-class={classes.selected}
        >
          Access Only
        </Typography>
      </div>
    );
  };

  const renderSubscribePlanContent = () => {
    return SubscribePlanContent.map((content) => (
      <div key={content.title} className={classes.planContentItem}>
        <StaticImage
          src={content.icons["1x"]}
          src2x={content.icons["2x"]}
          src3x={content.icons["3x"]}
          className={classes.planContentIcon}
        />
        <div className={classes.planContentText}>
          <Typography
            variant={
              isMobile
                ? TypographyVariant.BodyB4Regular
                : TypographyVariant.BodyB2Regular
            }
          >
            {content.title}
          </Typography>
          <Typography
            variant={
              isMobile
                ? TypographyVariant.BodyB6Regular
                : TypographyVariant.BodyB4Regular
            }
            color={TypographyColor.Silver}
          >
            {content.description}
          </Typography>
        </div>
      </div>
    ));
  };

  useEffect(() => {
    const dataGamepad = activeElement?.getAttribute("data-gamepad");
    if (dataGamepad?.startsWith("paywall-")) {
      setShowingPlan(
        dataGamepad === "paywall-bundle"
          ? "essential_pass_bundle"
          : "console_only",
      );
    }
  }, [activeElement]);

  useEffect(() => {
    AnalyticsGA4.paywallView(fromTo, "no_subscription");
  }, [fromTo]);

  if (isLoading) return <LoadingScreen />;

  return (
    <div className={classes.root}>
      {isDesktop && (
        <StaticImage src={SubscriptionTv} className={classes.background} />
      )}
      <div className={classes.header} onClick={handleClose}>
        <Icon
          name={"icon-linear-arrow-left-3"}
          className={classes.iconHeader}
          size={isDesktop ? 52 : 24}
          data-button-sound="back"
        />
      </div>
      <ActivateQrPopup
        onClose={() => setActivateQRLink("")}
        activateLink={activateQRLink}
      />
      <ConfirmSubscriptionPopup
        open={isConfirmOpen}
        price={selectedPlan?.metadata?.netAmount}
        onConfirm={confirmSubscription}
        onClose={closeSubscriptionFlow}
        gst={selectedPlan?.metadata?.taxRate}
        totalAmount={selectedPlan?.planAmount}
      />
      <BillingInfoPopup
        open={isBillingOpen}
        onContinue={submitBilling}
        onClose={closeSubscriptionFlow}
      />
      <StaticImage
        src={MainBG1x}
        src2x={MainBG2x}
        src3x={MainBG3x}
        className={classes.backgroundImage}
      />
      {isWithController && (
        <StaticImage
          src={Controller1x}
          src2x={Controller2x}
          src3x={Controller3x}
          className={classes.controllerImage}
        />
      )}
      <div className={clsx(classes.planWrapper, "header")}>
        <div className={classes.planTitleWrapper}>
          <p className={classes.planTitle}>PREMIUM GAMING</p>
          <p className={classes.planDescription}>ON ALL YOUR SCREENS</p>
        </div>
        {isDesktop && (
          <div className={classes.planContent}>
            {renderSubscribePlanContent()}
          </div>
        )}
      </div>
      {!isDesktop && (
        <SliderContainer
          nbSlide={4}
          isHideDots
          settings={{
            ...SLIDER_SETTINGS,
          }}
          className={classes.planSlider}
        >
          {renderSubscribePlanContent()}
        </SliderContainer>
      )}
      <div className={clsx(classes.planWrapper, classes.content)}>
        {!isFounder && renderPlanFeatures()}
        <div className={clsx(classes.planImage, { isWithController })}>
          <StaticImage
            src={MobileImage}
            src2x={MobileImage2x}
            src3x={MobileImage3x}
            className={classes.mobileImage}
          />
          {isWithController && (
            <StaticImage
              src={Controller1x}
              src2x={Controller2x}
              src3x={Controller3x}
              className={classes.controllerMobileImage}
            />
          )}
        </div>
        <div className={classes.planPayment}>
          {isWithController ? (
            <div className={classes.planPaymentTitle}>
              <p>3 month access</p>
              <p>+</p>
              <p>Pro Controller</p>
            </div>
          ) : (
            <div className={classes.planPaymentTitle}>
              <p>Console Access</p>
            </div>
          )}
          {isFounder && (
            <div className={classes.badgeContainer}>
              <Icon name="icon-light" size={16} />
              <Typography
                variant={TypographyVariant.BodyB6Bold}
                color={TypographyColor.Default}
              >
                FOUNDERS’ ACCESS
              </Typography>
            </div>
          )}
          {isWithController ? (
            <div className={classes.planPaymentPrice}>
              <Typography variant={TypographyVariant.BodyB4Regular}>
                Starting only at
              </Typography>
              <Typography variant={TypographyVariant.HeadingH1Bold}>
                <span className={classes.price}>₹54,990</span> ₹
                {firstPlan?.planAmount}
              </Typography>
              <Typography
                variant={TypographyVariant.BodyB5Medium}
                color={TypographyColor.Silver}
              >
                Without the expensive price tag
              </Typography>
            </div>
          ) : (
            <div className={classes.planPaymentPrice}>
              <Typography variant={TypographyVariant.BodyB4Regular}>
                Monthly
              </Typography>
              <div className={classes.priceContainer}>
                {withoutController?.metadata?.originalNetAmount &&
                  withoutController?.metadata?.originalNetAmount !==
                    withoutController?.metadata?.netAmount && (
                    <Typography
                      variant={TypographyVariant.BodyB2Regular}
                      color={TypographyColor.Gray}
                      className={classes.originalPrice}
                    >
                      ₹{withoutController?.metadata?.originalNetAmount}
                    </Typography>
                  )}
                <Typography variant={TypographyVariant.HeadingH1Bold}>
                  ₹{withoutController?.metadata?.netAmount}
                </Typography>
              </div>
            </div>
          )}
          <Button
            size="large"
            className={classes.getButton}
            onClick={
              isWithController
                ? subscribeProController
                : subscribeWithoutController
            }
          >
            {isWithController ? "Get It From " : "Subscribe Now"}
            {isWithController && (
              <StaticImage
                src={FlipkartLogo1x}
                src2x={FlipkartLogo2x}
                src3x={FlipkartLogo3x}
                className={classes.flipkartLogo}
              />
            )}
          </Button>
          <div className={classes.blury} />
        </div>
      </div>
      <div className={classes.bottomBar}>
        <Typography
          variant={
            isDesktop
              ? TypographyVariant.BodyB3Medium
              : TypographyVariant.BodyB6Medium
          }
          color={TypographyColor.Silver}
        >
          Already have a Pro Controller Bundle? Scan the QR code on your Smart
          Console access key.
        </Typography>
      </div>
      <AccountMismatchPopup
        isOpen={isMismatchPopupOpen}
        onClose={() => setIsMismatchPopupOpen(false)}
      />
    </div>
  );
};

export default Paywall;
